import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, errMsg, fmtDate, mediaUrl } from "@/lib/api";
import { PageTitle, Panel } from "@/pages/admin/AdminLayout";

const ST = { pending: ["Por rever", "text-[#e0b97f]"], approved: ["Publicada", "text-[#9fc79a]"], rejected: ["Rejeitada", "text-[#e08f7f]"] };

export function ReviewCard({ v: init, watches, compact }) {
  const [v, setV] = useState(init);
  const patch = async (body) => { try { setV((await api.patch(`/admin/reviews/${v.id}`, body)).data); toast.success("Opinião atualizada"); } catch (e) { toast.error(errMsg(e)); } };
  const [l, c] = ST[v.status];
  return (
    <div className="border border-[#282520] bg-[#141310] p-4 flex gap-4" data-testid={`review-card-${v.id}`}>
      {v.photo?.sm && <a href={mediaUrl(v.photo.lg || v.photo.md)} target="_blank" rel="noreferrer"><img src={mediaUrl(v.photo.sm)} alt="" className="w-20 h-24 object-cover" /></a>}
      <div className="flex-1 min-w-0 space-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => patch({ stars: n })} data-testid={`review-${v.id}-star-${n}`}><Star className={`w-3.5 h-3.5 ${n <= v.stars ? "fill-[#C8AE6E] text-[#C8AE6E]" : "text-[#5a554c]"}`} /></button>)}</span>
          <span className={`text-xs ${c}`} data-testid={`review-status-${v.id}`}>{l}</span>
          <span className="text-xs text-[#A39E93]">{v.source === "whatsapp" ? "via WhatsApp" : "via site"} · {v.lang?.toUpperCase()} · {fmtDate(v.created_at)}</span>
          {!compact && <Link to={`/admin/pedidos/${v.request_id}`} className="text-xs text-[#E6D5AC] hover:underline">{v.ref} · {v.first_name}</Link>}
        </div>
        {[["Experiência", v.experience], ["Qualidade", v.quality], ["Comentário", v.comment]].filter(([, t]) => t).map(([k, t]) => <p key={k} className="text-[#F3EFE6]/90"><span className="text-[#A39E93] text-xs">{k}: </span>{t}</p>)}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <select className="afield !w-full sm:!w-auto max-w-full !py-1 text-xs" value={v.watch_id || ""} onChange={(e) => patch({ watch_id: e.target.value })} data-testid={`review-watch-${v.id}`}>
            <option value="" disabled>Escolher relógio…</option>
            {watches.map((w) => <option key={w.id} value={w.id}>{w.brand} {w.model}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" className="accent-[#C8AE6E]" checked={v.consent_publish} onChange={(e) => patch({ consent_publish: e.target.checked })} data-testid={`review-consent-${v.id}`} />Cliente autorizou publicação</label>
          {v.status !== "approved" && <button className="btn-gold !py-1 !px-3 text-xs" onClick={() => patch({ status: "approved" })} data-testid={`review-approve-${v.id}`}>Publicar no site</button>}
          {v.status !== "rejected" && <button className="btn-line !py-1 !px-3 text-xs" onClick={() => patch({ status: "rejected" })} data-testid={`review-reject-${v.id}`}>{v.status === "approved" ? "Retirar" : "Rejeitar"}</button>}
        </div>
      </div>
    </div>
  );
}

export function useWatchOptions() {
  const [watches, setWatches] = useState([]);
  useEffect(() => { api.get("/admin/watches").then((x) => setWatches(Array.isArray(x.data) ? x.data : x.data.items || [])); }, []);
  return watches;
}

export function RequestReviews({ rid, refresh }) {
  const [items, setItems] = useState([]);
  const watches = useWatchOptions();
  useEffect(() => { api.get(`/admin/reviews?request_id=${rid}`).then((x) => setItems(x.data)); }, [rid, refresh]);
  if (!items.length) return null;
  return <Panel data-testid="request-reviews-panel"><h3 className="text-lg mb-3">Opinião do cliente</h3><div className="space-y-3">{items.map((v) => <ReviewCard key={v.id + v.created_at + (v.photo?.sm || "")} v={v} watches={watches} compact />)}</div></Panel>;
}

export default function Reviews() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const watches = useWatchOptions();
  useEffect(() => { let live = true; setItems(null); api.get(`/admin/reviews${status ? `?status=${status}` : ""}`).then((x) => live && setItems(x.data)); return () => { live = false; }; }, [status]);
  return (
    <div data-testid="admin-reviews">
      <PageTitle title="Opiniões" sub="Pedidas por WhatsApp dois dias depois da visita. Só aparecem no site depois de aprovadas e com autorização do cliente." />
      <div className="flex flex-wrap gap-2 mb-6">{[["pending", "Por rever"], ["approved", "Publicadas"], ["rejected", "Rejeitadas"], ["", "Todas"]].map(([k, l]) => <button key={k} className={`whitespace-nowrap ${status === k ? "btn-gold !py-1.5 !px-4 text-xs" : "btn-line !py-1.5 !px-4 text-xs"}`} onClick={() => setStatus(k)} data-testid={`reviews-filter-${k || "all"}`}>{l}</button>)}</div>
      {items && !items.length && <p className="text-sm text-[#A39E93]" data-testid="reviews-empty">Sem opiniões nesta lista.</p>}
      <div className="grid xl:grid-cols-2 gap-4">{(items || []).map((v) => <ReviewCard key={v.id} v={v} watches={watches} />)}</div>
    </div>
  );
}
